import React, { useEffect, useState, ButtonHTMLAttributes } from "react";
import { useLightboxState } from "yet-another-react-lightbox";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { generateClient } from "@aws-amplify/api";
import { UpdateArtInput, UpdateArtMutation, GetArtQuery } from "../../src/API";
import { getArtCustom } from "../../src/graphql/custom_queries";
import { updateArtCustom } from "../../src/graphql/custom_mutations";

interface LikeButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  filenames: string[];
}

const LikeButton: React.FC<LikeButtonProps> = ({ filenames, ...rest }) => {
  const [isLiked, setIsLiked] = useState<boolean | null>(null);
  const { currentIndex } = useLightboxState();
  const client = generateClient();

  useEffect(() => {
    // Define the function to fetch art data by ID
    async function getArtById(id: string) {
      try {
        const variables = { id };
        const response = await client.graphql({
          query: getArtCustom,
          variables,
        });

        return (response as { data: GetArtQuery }).data.getArt.like;
      } catch (error) {
        console.error("Error fetching art2:", error);
        throw error;
      }
    }
    const fetchLikeStatus = async () => {
      setIsLiked(await getArtById(filenames[currentIndex]));
    };
    if (filenames.length > 0) {
      fetchLikeStatus();
    }
  }, [filenames, currentIndex, client]);

  const updateLike = async () => {
    const input: UpdateArtInput = {
      id: filenames[currentIndex],
      like: isLiked === true ? null : true,
    };

    (await client.graphql({
      query: updateArtCustom,
      variables: {
        input,
      },
    })) as { data?: UpdateArtMutation; errors?: unknown[] };
  };

  const handleLikeClick = async () => {
    setIsLiked(isLiked === true ? null : true);
    await updateLike();
  };

  const updateDislike = async () => {
    const input: UpdateArtInput = {
      id: filenames[currentIndex],
      like: isLiked === false ? null : false,
    };

    (await client.graphql({
      query: updateArtCustom,
      variables: {
        input,
      },
    })) as { data?: UpdateArtMutation; errors?: unknown[] };
  };

  const handleDislikeClick = async () => {
    setIsLiked(isLiked === false ? null : false);
    await updateDislike();
  };

  return (
    <>
      <button
        type="button"
        className="thumb-up"
        onClick={handleLikeClick}
        {...rest}
      >
        {isLiked === true ? (
          <ThumbUpIcon className="ThumbUp" />
        ) : (
          <ThumbUpOutlinedIcon className="ThumbUp" />
        )}
        <style>{`
        .ThumbUp {
          color: black;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .ThumbUp:hover {
          color: 	#D3D3D3;
        }

        .thumb-up {
          position: relative; /* Add relative positioning */
          color: black;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          padding-top: 10px;
          padding-bottom: 10px;
          padding-right: 15px;
          padding-left: 15px;
        }
        
        @media (min-width: 600px) {
          .ThumbUp {
            height: 36px;
            width: 36px;
            color: black;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          .thumb-up {
            padding-top: 10px;
            padding-bottom: 10px;
            padding-right: 15px;
            padding-left: 15px;
          }
        }
      `}</style>
      </button>
      <button
        type="button"
        className="thumb-down"
        onClick={handleDislikeClick}
        {...rest}
      >
        {isLiked === false ? (
          <ThumbDownIcon className="ThumbDown" />
        ) : (
          <ThumbDownOutlinedIcon className="ThumbDown" />
        )}
        <style>{`
        .ThumbDown {
          color: black;
          border: none;
          border-radius: 4px;
          cursor: pointer;
        }

        .ThumbDown:hover {
          color: #D3D3D3;
        }

        .thumb-down {
          position: relative; /* Add relative positioning */
          color: black;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          padding-top: 10px;
          padding-bottom: 10px;
          padding-right: 15px;
          padding-left: 15px;
        }

        @media (min-width: 600px) {
          .ThumbDown {
            height: 36px;
            width: 36px;
            color: black;
            border: none;
            border-radius: 4px;
            cursor: pointer;
          }
          .thumb-down {
            padding-top: 10px;
            padding-bottom: 10px;
            padding-right: 15px;
            padding-left: 15px;
          }
        }
        }
      `}</style>
      </button>
    </>
  );
};

export default LikeButton;
